import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import type { Rank } from '../types';
import { RULES } from '../constants';

export class HUD {
  private container: HTMLElement;
  private startScreen!: HTMLElement;
  private gameOverScreen!: HTMLElement;
  private topBar!: HTMLElement;
  private kpiRow!: HTMLElement;
  private bottomBar!: HTMLElement;
  private comboDisplay!: HTMLElement;

  // Cached elements for fast updates
  private apmValue!: HTMLElement;
  private scoreValue!: HTMLElement;
  private timeValue!: HTMLElement;
  private computeValue!: HTMLElement;
  private livesContainer!: HTMLElement;
  private drValue!: HTMLElement;
  private vsValue!: HTMLElement;
  private ceValue!: HTMLElement;
  private taValue!: HTMLElement;
  private spawnBtn!: HTMLButtonElement;
  private despawnBtn!: HTMLButtonElement;
  private spawnCostEl!: HTMLElement;
  private comboText!: HTMLElement;

  // Game over elements
  private goScore!: HTMLElement;
  private goStats!: HTMLElement;
  private goRank!: HTMLElement;

  private onPlay: () => void;
  private onRestart: () => void;
  private onSpawn: () => void;
  private onDespawn: () => void;
  private onMute: () => boolean;
  private onTutorial: () => void;
  private onMenu: () => void;
  private onGetLeaderboard: () => Promise<Array<{ playerName: string; score: number; rank: string; apm: number; survivalTime: number; peakAgents: number }>>;
  private onSubmitScore: (name: string) => Promise<void>;

  constructor(
    container: HTMLElement,
    callbacks: {
      onPlay: () => void;
      onTutorial: () => void;
      onRestart: () => void;
      onMenu: () => void;
      onSpawn: () => void;
      onDespawn: () => void;
      onMute: () => boolean;
      onGetLeaderboard: () => Promise<Array<{ playerName: string; score: number; rank: string; apm: number; survivalTime: number; peakAgents: number }>>;
      onSubmitScore: (name: string) => Promise<void>;
    }
  ) {
    this.container = container;
    this.onPlay = callbacks.onPlay;
    this.onTutorial = callbacks.onTutorial;
    this.onRestart = callbacks.onRestart;
    this.onMenu = callbacks.onMenu;
    this.onSpawn = callbacks.onSpawn;
    this.onDespawn = callbacks.onDespawn;
    this.onMute = callbacks.onMute;
    this.onGetLeaderboard = callbacks.onGetLeaderboard;
    this.onSubmitScore = callbacks.onSubmitScore;

    this.createDOM();
  }

  private createDOM(): void {
    const hud = document.createElement('div');
    hud.id = 'hud';

    // Start screen
    this.startScreen = document.createElement('div');
    this.startScreen.className = 'start-screen';
    this.startScreen.innerHTML = `
      <div class="start-title">VIBE CODING GYM</div>
      <div class="start-subtitle">Train your multitasking skills, become the best AI Agents manager</div>
      <div class="start-buttons">
        <button class="btn-play" id="btn-tutorial">TUTORIAL</button>
        <button class="btn-play" id="btn-survival">SURVIVAL</button>
      </div>
      <div class="start-links">
        <button class="btn-philosophy" id="btn-leaderboard">LEADERBOARD</button>
        <button class="btn-philosophy" id="btn-philosophy">PHILOSOPHY</button>
        <a class="btn-philosophy interactive" href="https://jam.pieter.com/portal/2026" style="pointer-events:auto;">VIBE JAM PORTAL</a>
      </div>
      <div class="leaderboard-panel hidden" id="leaderboard-panel">
        <div class="leaderboard-content glass-panel">
          <div class="philosophy-close interactive" id="leaderboard-close">X</div>
          <h2>LEADERBOARD</h2>
          <div class="lb-podium" id="lb-podium"></div>
          <div class="lb-table-header">
            <span class="lbh-rank">#</span>
            <span class="lbh-name">PLAYER</span>
            <span class="lbh-score">SCORE</span>
            <span class="lbh-apm">APM</span>
            <span class="lbh-time">TIME</span>
            <span class="lbh-agents">AGENTS</span>
          </div>
          <div class="lb-table lb-scrollable" id="menu-lb-table">Loading...</div>
          <div class="lb-total" id="lb-total"></div>
        </div>
      </div>
      <div class="philosophy-panel hidden" id="philosophy-panel">
        <div class="philosophy-content glass-panel">
          <div class="philosophy-close interactive" id="philosophy-close">X</div>
          <h2>Why this game exists</h2>
          <p>People say AI is for the lazy. That's wrong. It's a complete mindset shift.</p>
          <p>We went from a <strong>sequential world</strong> — one task, one person, start to finish — to a <strong>massively parallel world</strong> where you orchestrate 5, 10, 20 AI agents simultaneously.</p>
          <p>This is <em>harder</em>, not easier. You need to dispatch work, monitor quality, catch errors, validate output — all at the same time. Your brain becomes the bottleneck, not your typing speed.</p>
          <p>The result? You produce <strong>10x more</strong>. But it's also <strong>10x more exhausting</strong>. Managing a swarm of agents is a new skill that didn't exist before.</p>
          <p>VIBE CODING GYM trains exactly that. One mode. One metric. How many agents can you handle before your brain crashes?</p>
          <p class="philosophy-tagline">Welcome to the parallel era.</p>
        </div>
      </div>
    `;
    this.startScreen.querySelector('#btn-tutorial')!.addEventListener('click', () => this.onTutorial());
    this.startScreen.querySelector('#btn-survival')!.addEventListener('click', () => this.onPlay());
    this.startScreen.querySelector('#btn-philosophy')!.addEventListener('click', () => {
      document.getElementById('philosophy-panel')!.classList.remove('hidden');
    });
    this.startScreen.querySelector('#philosophy-close')!.addEventListener('click', () => {
      document.getElementById('philosophy-panel')!.classList.add('hidden');
    });
    this.startScreen.querySelector('#btn-leaderboard')!.addEventListener('click', () => {
      document.getElementById('leaderboard-panel')!.classList.remove('hidden');
      this.loadMenuLeaderboard();
    });
    this.startScreen.querySelector('#leaderboard-close')!.addEventListener('click', () => {
      document.getElementById('leaderboard-panel')!.classList.add('hidden');
    });
    hud.appendChild(this.startScreen);

    // Top bar
    this.topBar = document.createElement('div');
    this.topBar.className = 'top-bar glass-panel hidden';
    this.topBar.innerHTML = `
      <div style="display:flex;gap:16px;align-items:center;">
        <div class="stat"><span class="stat-label">APM</span><span class="stat-value apm" id="hud-apm">100</span></div>
        <div class="stat"><span class="stat-label">SCORE</span><span class="stat-value score" id="hud-score">0</span></div>
        <div class="stat"><span class="stat-label">TIME</span><span class="stat-value time" id="hud-time">0:00</span></div>
      </div>
      <div style="display:flex;gap:16px;align-items:center;">
        <div class="stat"><span class="stat-label">COMPUTE</span><span class="stat-value compute" id="hud-compute">0</span></div>
        <div class="lives" id="hud-lives"></div>
        <button class="btn btn-menu interactive" id="hud-menu">[ESC] MENU</button>
      </div>
    `;
    hud.appendChild(this.topBar);

    // KPI row
    this.kpiRow = document.createElement('div');
    this.kpiRow.className = 'kpi-row glass-panel hidden';
    this.kpiRow.innerHTML = `
      <div class="kpi"><span class="kpi-label">DR:</span><span class="kpi-value" id="hud-dr">100</span></div>
      <div class="kpi"><span class="kpi-label">VS:</span><span class="kpi-value" id="hud-vs">100</span></div>
      <div class="kpi"><span class="kpi-label">CE:</span><span class="kpi-value" id="hud-ce">100</span></div>
      <div class="kpi"><span class="kpi-label">TA:</span><span class="kpi-value" id="hud-ta">100</span></div>
    `;
    hud.appendChild(this.kpiRow);

    // Combo display
    this.comboDisplay = document.createElement('div');
    this.comboDisplay.className = 'combo-display glass-panel hidden';
    this.comboDisplay.innerHTML = `<span id="hud-combo">COMBO: x0</span>`;
    hud.appendChild(this.comboDisplay);

    // Bottom bar
    this.bottomBar = document.createElement('div');
    this.bottomBar.className = 'bottom-bar glass-panel hidden';
    this.bottomBar.innerHTML = `
      <div class="queue-display" id="hud-queue"></div>
      <div class="spawn-controls">
        <button class="btn btn-spawn" id="hud-spawn">[S] SPAWN<span class="spawn-cost" id="hud-spawn-cost">100</span></button>
        <button class="btn btn-despawn" id="hud-despawn">[D] DESPAWN</button>
        <button class="btn btn-minigame interactive" id="hud-aim">[A] AIM BOOST</button>
        <button class="btn btn-minigame interactive" id="hud-math">[M] MATH +LIFE</button>
        <button class="btn" id="hud-mute" style="padding:8px 10px;">[Q] SOUND ON</button>
      </div>
    `;
    hud.appendChild(this.bottomBar);

    // Game over screen — two phases: submit name, then see results
    this.gameOverScreen = document.createElement('div');
    this.gameOverScreen.className = 'gameover-screen hidden';
    this.gameOverScreen.innerHTML = `
      <div class="gameover-title">SYSTEM FAILURE</div>
      <div class="gameover-rank" id="go-rank">D</div>
      <div class="gameover-score" id="go-score">0</div>
      <div class="gameover-stats" id="go-stats"></div>

      <div class="go-submit-section" id="go-submit-section">
        <div class="go-submit-label">Save your score? (optional)</div>
        <div class="go-submit-row">
          <input type="text" id="go-name" class="glass-panel interactive go-name-input" placeholder="Anonymous" maxlength="20" value="" />
          <button class="btn-play interactive" id="go-submit-btn">SUBMIT</button>
        </div>
        <button class="btn interactive" id="go-skip" style="font-size:11px;color:#888;border-color:rgba(255,255,255,0.15);margin-top:4px;">SKIP</button>
      </div>

      <div class="go-results-section hidden" id="go-results-section">
        <div class="gameover-leaderboard" id="go-leaderboard"></div>
        <div class="gameover-buttons">
          <button class="btn-play interactive" id="go-restart">RESTART</button>
          <button class="btn interactive" id="go-share" style="border-color:rgba(96,165,250,0.4);color:#60a5fa;">SHARE</button>
          <button class="btn interactive" id="go-menu" style="border-color:rgba(167,139,250,0.4);color:#a78bfa;">MENU</button>
        </div>
      </div>
    `;
    hud.appendChild(this.gameOverScreen);

    this.container.appendChild(hud);

    // Cache elements
    this.apmValue = document.getElementById('hud-apm')!;
    this.scoreValue = document.getElementById('hud-score')!;
    this.timeValue = document.getElementById('hud-time')!;
    this.computeValue = document.getElementById('hud-compute')!;
    this.livesContainer = document.getElementById('hud-lives')!;
    this.drValue = document.getElementById('hud-dr')!;
    this.vsValue = document.getElementById('hud-vs')!;
    this.ceValue = document.getElementById('hud-ce')!;
    this.taValue = document.getElementById('hud-ta')!;
    this.spawnBtn = document.getElementById('hud-spawn') as HTMLButtonElement;
    this.despawnBtn = document.getElementById('hud-despawn') as HTMLButtonElement;
    this.spawnCostEl = document.getElementById('hud-spawn-cost')!;
    this.comboText = document.getElementById('hud-combo')!;
    this.goScore = document.getElementById('go-score')!;
    this.goStats = document.getElementById('go-stats')!;
    this.goRank = document.getElementById('go-rank')!;

    // Wire buttons
    this.spawnBtn.addEventListener('click', () => this.onSpawn());
    this.despawnBtn.addEventListener('click', () => this.onDespawn());
    document.getElementById('go-restart')!.addEventListener('click', () => this.onRestart());
    document.getElementById('go-menu')!.addEventListener('click', () => this.onMenu());
    document.getElementById('hud-menu')!.addEventListener('click', () => this.onMenu());
    // Submit score → show results
    document.getElementById('go-submit-btn')!.addEventListener('click', () => this.handleScoreSubmit());
    document.getElementById('go-skip')!.addEventListener('click', () => {
      document.getElementById('go-submit-section')?.classList.add('hidden');
      document.getElementById('go-results-section')?.classList.remove('hidden');
    });
    document.getElementById('go-name')!.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') this.handleScoreSubmit();
    });
    document.getElementById('hud-mute')!.addEventListener('click', () => {
      const muted = this.onMute();
      document.getElementById('hud-mute')!.textContent = muted ? '[Q] SOUND OFF' : '[Q] SOUND ON';
    });
    // Mini-game buttons → simulate keyboard press
    document.getElementById('hud-aim')!.addEventListener('click', () => {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'a' }));
    });
    document.getElementById('hud-math')!.addEventListener('click', () => {
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'm' }));
    });

    // Init lives
    this.updateLives(RULES.maxLives);
  }

  showStartScreen(): void {
    this.startScreen.classList.remove('hidden');
    this.topBar.classList.add('hidden');
    this.kpiRow.classList.add('hidden');
    this.bottomBar.classList.add('hidden');
    this.comboDisplay.classList.add('hidden');
    this.gameOverScreen.classList.add('hidden');
  }

  showGameplay(): void {
    this.startScreen.classList.add('hidden');
    this.topBar.classList.remove('hidden');
    this.kpiRow.classList.remove('hidden');
    this.bottomBar.classList.remove('hidden');
    this.gameOverScreen.classList.add('hidden');

    // First-run labels (only on first ever game)
    if (!localStorage.getItem('vcg_played')) {
      localStorage.setItem('vcg_played', '1');
      this.showFirstRunLabels();
    }
  }

  private showFirstRunLabels(): void {
    const labels = document.createElement('div');
    labels.className = 'first-run-labels';
    labels.innerHTML = `
      <div class="first-run-label label-spawn">SPAWN AGENTS</div>
      <div class="first-run-label label-queue">TASK QUEUE</div>
      <div class="first-run-label label-agents">CLICK AGENTS TO CORRECT DRIFT</div>
      <div class="first-run-label label-tasks">CLICK TASKS TO ASSIGN</div>
    `;
    document.getElementById('hud')?.appendChild(labels);

    // Fade after 8s, remove after 10s
    setTimeout(() => labels.classList.add('fading'), 8000);
    setTimeout(() => labels.remove(), 10000);
  }

  showGameOver(score: number, apm: number, time: number, peakAgents: number, rank: Rank): void {
    this.gameOverScreen.classList.remove('hidden');
    this.goScore.textContent = score.toLocaleString();
    this.goRank.textContent = `RANK: ${rank}`;
    this.goStats.innerHTML = `
      <span>APM: ${apm}</span>
      <span>TIME: ${this.formatTime(time)}</span>
      <span>PEAK AGENTS: ${peakAgents}</span>
    `;
    // Reset to submit phase
    document.getElementById('go-submit-section')?.classList.remove('hidden');
    document.getElementById('go-results-section')?.classList.add('hidden');
    // Focus the name input
    setTimeout(() => (document.getElementById('go-name') as HTMLInputElement)?.focus(), 100);
  }

  update(state: GameState): void {
    this.apmValue.textContent = String(state.kpis.apm);
    this.scoreValue.textContent = state.score.toLocaleString();
    this.timeValue.textContent = this.formatTime(state.time);
    this.computeValue.textContent = String(Math.floor(state.compute));

    this.drValue.textContent = String(state.kpis.dr);
    this.vsValue.textContent = String(state.kpis.vs);
    this.ceValue.textContent = String(state.kpis.ce);
    this.taValue.textContent = String(state.kpis.ta);

    // Spawn button state
    const cost = state.getNextSpawnCost();
    this.spawnCostEl.textContent = String(cost);
    if (state.canSpawnAgent()) {
      this.spawnBtn.classList.remove('disabled');
    } else {
      this.spawnBtn.classList.add('disabled');
    }

    // Despawn button
    if (state.agents.length > 0) {
      this.despawnBtn.classList.remove('disabled');
    } else {
      this.despawnBtn.classList.add('disabled');
    }

    // Combo
    if (state.combo > 1) {
      this.comboDisplay.classList.remove('hidden');
      this.comboDisplay.classList.add('active');
      this.comboText.textContent = `COMBO: x${state.combo}`;
    } else {
      this.comboDisplay.classList.remove('active');
    }
  }

  updateLives(lives: number): void {
    this.livesContainer.innerHTML = '<span class="lives-label">LIVES</span>';
    for (let i = 0; i < RULES.maxLives; i++) {
      const life = document.createElement('div');
      life.className = `life ${i < lives ? 'active' : 'lost'}`;
      this.livesContainer.appendChild(life);
    }
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  private async handleScoreSubmit(): Promise<void> {
    const nameInput = document.getElementById('go-name') as HTMLInputElement;
    const name = nameInput?.value.trim() || 'Anonymous';
    const submitBtn = document.getElementById('go-submit-btn') as HTMLButtonElement;
    submitBtn.textContent = 'SAVING...';
    submitBtn.disabled = true;

    await this.onSubmitScore(name);

    // Hide submit section, show results
    document.getElementById('go-submit-section')?.classList.add('hidden');
    document.getElementById('go-results-section')?.classList.remove('hidden');

    // Load leaderboard (compact for game over)
    const scores = await this.onGetLeaderboard();
    const lbEl = document.getElementById('go-leaderboard');
    if (lbEl && scores.length > 0) {
      const rows = scores.slice(0, 10).map((s, i) =>
        `<tr><td class="lb-rank">#${i + 1}</td><td class="lb-name">${s.playerName}</td><td class="lb-score">${s.score.toLocaleString()}</td><td class="lb-apm">${s.apm}</td><td class="lb-time">${this.fmtTime(s.survivalTime)}</td></tr>`
      ).join('');
      lbEl.innerHTML = `<table>${rows}</table>`;
    }

    // Reset submit button for next game
    submitBtn.textContent = 'SUBMIT';
    submitBtn.disabled = false;
  }

  private fmtTime(s: number): string {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  private async loadMenuLeaderboard(): Promise<void> {
    const tableEl = document.getElementById('menu-lb-table');
    const podiumEl = document.getElementById('lb-podium');
    const totalEl = document.getElementById('lb-total');
    if (!tableEl) return;
    tableEl.textContent = 'Loading...';
    if (podiumEl) podiumEl.innerHTML = '';

    const scores = await this.onGetLeaderboard();

    if (scores.length === 0) {
      tableEl.innerHTML = '<div class="lb-empty">No scores yet. Be the first!</div>';
      return;
    }

    // Podium: top 3
    if (podiumEl && scores.length >= 1) {
      const medals = ['🥇', '🥈', '🥉'];
      const podiumClasses = ['podium-gold', 'podium-silver', 'podium-bronze'];
      const top3 = scores.slice(0, 3);
      podiumEl.innerHTML = top3.map((s, i) =>
        `<div class="podium-entry ${podiumClasses[i]}">
          <div class="podium-medal">${medals[i]}</div>
          <div class="podium-name">${s.playerName}</div>
          <div class="podium-score">${s.score.toLocaleString()}</div>
          <div class="podium-details">APM ${s.apm} · ${this.fmtTime(s.survivalTime)} · ${s.peakAgents} agents</div>
        </div>`
      ).join('');
    }

    // Table: all scores (skip top 3 if podium shown)
    const startIdx = podiumEl ? 3 : 0;
    const rest = scores.slice(startIdx);
    if (rest.length === 0) {
      tableEl.innerHTML = '';
    } else {
      const rows = rest.map((s, i) =>
        `<tr>
          <td class="lb-rank">#${startIdx + i + 1}</td>
          <td class="lb-name">${s.playerName}</td>
          <td class="lb-score">${s.score.toLocaleString()}</td>
          <td class="lb-apm">${s.apm}</td>
          <td class="lb-time">${this.fmtTime(s.survivalTime)}</td>
          <td class="lb-agents">${s.peakAgents}</td>
        </tr>`
      ).join('');
      tableEl.innerHTML = `<table>${rows}</table>`;
    }

    if (totalEl) {
      totalEl.textContent = `${scores.length} players`;
    }
  }

  dispose(): void {
    const hud = document.getElementById('hud');
    if (hud) hud.remove();
  }
}
